import {
  Children,
  isValidElement,
  useRef,
  useEffect,
  cloneElement,
  createContext,
  useContext,
  forwardRef,
  useState,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ListSubheader from "@material-ui/core/ListSubheader";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import { VariableSizeList } from "react-window";
import { formatOfficerName } from "../utils/utils";
import { shouldBeLocked } from "../utils/Roles";
import clsx from "clsx";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef(function OuterElementType(props, ref) {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = child => {
    if (isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          style={{ display: "flex", whiteSpace: "nowrap" }}
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const useStyles = makeStyles(theme => ({
  listbox: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
      lineHeight: "1rem",
    },
  },
  formControl: {
    // margin: theme.spacing(2),
    minWidth: 120,
    margin: theme.spacing(1),
    whiteSpace: "nowrap",
    display: "flex",
    justifyContent: "center",
  },
}));

const renderGroup = params => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

const CustomAutoComplete = ({
  minWidth = "250px",
  addMargin = true,
  title,
  options = [],
  loading = false,
  error = "",
  value = {},
  name = "name",
  stateName,
  updateFields,
  nameFormatter = formatOfficerName,
  id,
  disabled = undefined,
  userRoles,
  customClass = "",
  multiple = false,
  limitTags = -1,
  groupBy = null,
  autoSelect = true,
  disableClearable = false,
  setPausePolling = null,
  allowAdd = false,
  renderOption = null,
  matchFromStart = false,
  disableCloseOnSelect = false,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const classes = useStyles();

  const filter = createFilterOptions(
    matchFromStart ? { matchFrom: "start" } : {}
  );

  if (multiple && !setPausePolling) {
    alert(`Pass setPausePolling to ${title}`);
  }

  const [autocompleteState, setAutocompleteState] = useState({
    stateOptions: [],
    stateValue: value,
  });

  useEffect(() => {
    if (options) {
      const sortedOptions = [...options].sort((a, b) => {
        if (a.position && b.position) {
          return a.position - b.position;
        }
        return 0;
      });

      setAutocompleteState(prevState => {
        return { ...prevState, stateOptions: sortedOptions };
      });
    }
  }, [options]);

  return options ? (
    <Autocomplete
      disableCloseOnSelect={disableCloseOnSelect}
      onFocus={() =>
        multiple && setPausePolling ? setPausePolling(true) : null
      }
      onBlur={() =>
        multiple && setPausePolling ? setPausePolling(false) : null
      }
      blurOnSelect={false}
      disableClearable={disableClearable}
      autoSelect={autoSelect}
      style={{ minWidth: minWidth }}
      disabled={memoizedLocked ? true : loading || error !== "" ? true : false}
      id={`autocomplete-${title}-${id}`}
      disableListWrap
      className={
        addMargin ? clsx(classes.formControl, customClass) : customClass
      }
      ListboxComponent={ListboxComponent}
      renderGroup={renderGroup}
      options={autocompleteState.stateOptions}
      getOptionSelected={(option, value) => option.id === value.id}
      value={value}
      multiple={multiple}
      limitTags={limitTags}
      //This will add the group headings
      // groupBy={(option) => option[0].toUpperCase()}
      renderInput={params => (
        <TextField
          {...params}
          data-cy={`${stateName}-autocomplete`}
          label={title}
          inputProps={{
            ...params.inputProps,
            "data-cy": `${stateName}-autocomplete-input`,
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}
      renderOption={renderOption}
      getOptionLabel={option =>
        nameFormatter && nameFormatter(option)
          ? nameFormatter(option)
          : option?.[name]
          ? option?.[name]
          : ""
      }
      onChange={(event, value) => updateFields(value, stateName)}
      groupBy={groupBy}
      autoHighlight={true}
      filterOptions={(options, params) => {
        const filtered = options ? filter(options, params) : [];
        // Suggest the creation of a new value
        if (params.inputValue !== "" && allowAdd) {
          filtered.push({
            value: `Add "${params.inputValue}"`,
            name: params.inputValue,
            action: "Add",
          });
        }

        return filtered;
      }}
    />
  ) : (
    <p>Loading</p>
  );
};

CustomAutoComplete.propTypes = {
  options: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default CustomAutoComplete;
